import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accounts, type Account } from '@/api/documents';
import { useDismissable } from '@/hooks/useDismissable';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const ACCOUNT_TYPE_LABEL: Record<string, string> = {
  A: 'Активний',
  P: 'Пасивний',
  AP: 'Активно-пасивний',
};

export default function ChartOfAccountsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);

  const list = useQuery({
    queryKey: ['accounts-all', showAll, q],
    queryFn: () => accounts.list({
      chart_code: 'HOZRASCHETNY',
      only_postable: !showAll,
      q: q || undefined,
      limit: 2000,
    }),
  });

  const updateMut = useMutation({
    mutationFn: (args: { id: string; do_not_revaluate: boolean }) =>
      accounts.update(args.id, { do_not_revaluate: args.do_not_revaluate }),
    onMutate: async (args) => {
      // Optimistic update — мгновенный отклик в UI
      await qc.cancelQueries({ queryKey: ['accounts-all', showAll, q] });
      const prev = qc.getQueryData<Account[]>(['accounts-all', showAll, q]);
      if (prev) {
        qc.setQueryData<Account[]>(['accounts-all', showAll, q],
          prev.map((a) => a.id === args.id
            ? { ...a, do_not_revaluate: args.do_not_revaluate }
            : a));
      }
      return { prev };
    },
    onError: (err, _args, ctx) => {
      // Откатываем optimistic, если запрос упал
      if (ctx?.prev) qc.setQueryData(['accounts-all', showAll, q], ctx.prev);
      alert('Помилка: ' + String(err));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['accounts-all'] });
    },
  });

  // Отдельная мутация для текстовых полей (через модалку).
  const editMut = useMutation({
    mutationFn: (args: { id: string; name: string; description: string | null }) =>
      accounts.update(args.id, { name: args.name, description: args.description }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts-all'] });
      setEditing(null);
    },
    onError: (err) => alert('Помилка збереження: ' + String(err)),
  });

  const cols = useMemo<ColumnDef<Account>[]>(() => [
    { key: 'code', header: 'Код', width: 110, sortValue: (r) => r.code ?? '', cell: (r) => r.code },
    { key: 'name', header: 'Найменування', sortValue: (r) => r.name ?? '', cell: (r) => r.name },
    {
      key: 'account_type', header: 'Тип', width: 140,
      sortValue: (r) => r.account_type ?? '',
      cell: (r) => ACCOUNT_TYPE_LABEL[r.account_type] ?? r.account_type,
    },
    {
      key: 'use_currency', header: 'Валюта', width: 90, align: 'center',
      sortValue: (r) => (r.use_currency ? 1 : 0),
      cell: (r) => (r.use_currency ? '✓' : ''),
    },
    {
      key: 'use_quantity', header: 'Кільк.', width: 90, align: 'center',
      sortValue: (r) => (r.use_quantity ? 1 : 0),
      cell: (r) => (r.use_quantity ? '✓' : ''),
    },
    {
      key: 'do_not_revaluate', header: 'Без переоцінки', width: 150, align: 'center',
      sortValue: (r) => (r.do_not_revaluate ? 1 : 0),
      cell: (r) => (
        <input
          type="checkbox"
          checked={Boolean(r.do_not_revaluate)}
          disabled={updateMut.isPending || !r.use_currency}
          title="Не підлягає переоцінці валюти (ПБО-21: аванси та інші немонетарні статті)"
          onChange={(e) => updateMut.mutate({ id: r.id, do_not_revaluate: e.target.checked })}
          style={{ cursor: updateMut.isPending || !r.use_currency ? 'not-allowed' : 'pointer' }}
        />
      ),
    },
    {
      key: 'is_group_only', header: 'Група', width: 90, align: 'center',
      sortValue: (r) => (r.is_group_only ? 1 : 0),
      cell: (r) => (r.is_group_only ? '📁' : ''),
    },
  ], [updateMut]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>План рахунків (Господарський)</h2>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <input
            placeholder="Пошук за кодом/назвою..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 280 }}
          />
          <label style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
            показувати групи
          </label>
        </div>
      </div>

      <DataTable<Account>
        rows={list.data ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає рахунків."
        onRowDoubleClick={(r) => setEditing(r)}
        initialSortKey="code"
      />

      <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
        <strong>Подвійний клік</strong> на рядку — редагування найменування й опису.
        <br />
        <strong>Без переоцінки</strong> — позначте рахунки, які <strong>не</strong>{' '}
        повинні переоцінюватись при курсових різницях. За ПБО-21 «Вплив змін
        валютних курсів» це немонетарні статті: аванси (3711/3712/6811/6812)
        та інші статті, що фіксуються за курсом дати першої події. Зміна
        прапора впливає на наступні документи «Переоцінка валюти» —
        раніше проведені переоцінки не змінюються автоматично.
      </p>

      {editing && (
        <EditAccountModal
          account={editing}
          isSaving={editMut.isPending}
          onCancel={() => setEditing(null)}
          onSave={(name, description) =>
            editMut.mutate({ id: editing.id, name, description })
          }
        />
      )}
    </div>
  );
}


interface EditAccountModalProps {
  account: Account;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (name: string, description: string | null) => void;
}

function EditAccountModal({ account, isSaving, onCancel, onSave }: EditAccountModalProps) {
  const [name, setName] = useState(account.name);
  const [description, setDescription] = useState(account.description ?? '');

  // Если открыли другую карточку без размонтирования — синхронизируем состояние.
  useEffect(() => {
    setName(account.name);
    setDescription(account.description ?? '');
  }, [account.id, account.name, account.description]);

  const trimmedName = name.trim();
  const canSave = trimmedName.length > 0 && !isSaving;
  const dirty = trimmedName !== account.name || (description || null) !== (account.description ?? null);

  const { tryDismiss } = useDismissable({
    enabled: true,
    dirty,
    onDismiss: onCancel,
  });

  return (
    <div
      onClick={tryDismiss}
      style={{
        position: 'fixed', inset: 0, background: 'var(--overlay)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-panel)', borderRadius: 8, padding: '1.25rem 1.5rem',
          width: 560, maxWidth: '90vw', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
      >
        <h3 style={{ marginTop: 0 }}>
          Редагування рахунку <span style={{ color: '#3b82f6' }}>{account.code}</span>
        </h3>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
          Тип: {account.account_type} ·
          {account.use_currency ? ' валютний' : ' без валюти'} ·
          {account.use_quantity ? ' з кількістю' : ' без кількості'} ·
          {account.is_group_only ? ' група' : ' звичайний'}
        </div>

        <div className="field">
          <label>Найменування</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            disabled={isSaving}
          />
        </div>

        <div className="field">
          <label>Опис</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            disabled={isSaving}
            style={{ width: '100%', fontFamily: 'inherit' }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.75rem' }}>
          <button onClick={tryDismiss} disabled={isSaving}>Скасувати</button>
          <button
            className="primary"
            disabled={!canSave || !dirty}
            onClick={() => onSave(trimmedName, description.trim() || null)}
          >
            {isSaving ? 'Збереження…' : 'Зберегти'}
          </button>
        </div>
      </div>
    </div>
  );
}
